import { Field, FieldInput, FieldLabel, FieldSelect, FieldTextarea } from '@ibe/ui';
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';

import { PropertySelect } from '../../../components/form/property-select.js';
import { type RoomTypeFormInput, ROOM_TYPE_STATUS_OPTIONS } from '../api/schemas.js';

export interface RoomTypeFieldsProps {
  readonly register: UseFormRegister<RoomTypeFormInput>;
  readonly errors: FieldErrors<RoomTypeFormInput>;
  readonly control: Control<RoomTypeFormInput>;
  readonly mode: 'create' | 'edit';
}

const BED_TYPE_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'SINGLE', label: 'Single' },
  { value: 'DOUBLE', label: 'Double' },
  { value: 'QUEEN', label: 'Queen' },
  { value: 'KING', label: 'King' },
  { value: 'TWIN', label: 'Twin' },
  { value: 'SOFA_BED', label: 'Sofa bed' },
  { value: 'BUNK', label: 'Bunk' },
  { value: 'COT', label: 'Cot' },
];

function Section({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <fieldset className="space-y-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <div className="space-y-1">
        <h2 className="text-body font-semibold">{title}</h2>
        {description === undefined ? null : (
          <p className="text-caption text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </fieldset>
  );
}

function Row({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}

export function RoomTypeFields({
  register,
  errors,
  control,
  mode,
}: RoomTypeFieldsProps): React.JSX.Element {
  return (
    <div className="space-y-6">
      <Section
        title="Identity"
        description="The property this room type belongs to, and how it is named in bookings."
      >
        <Field error={errors.propertyId?.message} required>
          <Controller
            control={control}
            name="propertyId"
            render={({ field }) => (
              <PropertySelect
                value={field.value}
                onChange={field.onChange}
                error={errors.propertyId?.message}
                required
                readOnly={mode === 'edit'}
                description={
                  mode === 'edit'
                    ? 'A room type cannot be moved to another property once it is created.'
                    : 'Choose the property this room type belongs to.'
                }
              />
            )}
          />
        </Field>

        <Row>
          <Field
            error={errors.code?.message}
            required
            description={
              mode === 'edit'
                ? 'Fixed once the room type exists — it appears in every booking reference already issued.'
                : 'Short, stable and yours. It appears in every booking reference.'
            }
          >
            <FieldLabel>Room type code</FieldLabel>
            <FieldInput
              autoComplete="off"
              placeholder="DLX-KING"
              readOnly={mode === 'edit'}
              className={mode === 'edit' ? 'bg-muted' : undefined}
              {...register('code')}
            />
          </Field>

          <Field error={errors.name?.message} required>
            <FieldLabel>Room type name</FieldLabel>
            <FieldInput autoComplete="off" placeholder="Deluxe King" {...register('name')} />
          </Field>
        </Row>

        <Row>
          <Field
            error={errors.displayName?.message}
            description="Shown to guests when it should differ from the internal name."
          >
            <FieldLabel>Display name</FieldLabel>
            <FieldInput autoComplete="off" placeholder="King Room" {...register('displayName')} />
          </Field>

          <Field
            error={errors.bedType?.message}
            description="Choose the bed this room is sold with."
          >
            <FieldLabel>Bed type</FieldLabel>
            <Controller
              control={control}
              name="bedType"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={BED_TYPE_OPTIONS}
                  placeholder="Not set"
                  searchable
                />
              )}
            />
          </Field>
        </Row>

        <Field error={errors.description?.message}>
          <FieldLabel>Description</FieldLabel>
          <FieldTextarea {...register('description')} />
        </Field>
      </Section>

      <Section
        title="Configuration"
        description="How many people it sleeps, the size, and how it is represented in a PMS."
      >
        <Row>
          <Field error={errors.maxOccupancy?.message} required>
            <FieldLabel>Maximum occupancy</FieldLabel>
            <FieldInput
              type="number"
              min="1"
              autoComplete="off"
              placeholder="2"
              {...register('maxOccupancy')}
            />
          </Field>

          <Field
            error={errors.sizeInSqm?.message}
            description="The room floor area in square metres."
          >
            <FieldLabel>Size (m²)</FieldLabel>
            <FieldInput autoComplete="off" placeholder="32.50" {...register('sizeInSqm')} />
          </Field>
        </Row>

        <Row>
          <Field
            error={errors.totalNoOfRooms?.message}
            description="The total count of physical rooms of this type."
          >
            <FieldLabel>Total number of rooms</FieldLabel>
            <FieldInput
              type="number"
              min="0"
              autoComplete="off"
              placeholder="0"
              {...register('totalNoOfRooms')}
            />
          </Field>

          <Field
            error={errors.pmsRoomTypeId?.message}
            description="The id this room type is known by in the connected PMS."
          >
            <FieldLabel>PMS room type id</FieldLabel>
            <FieldInput autoComplete="off" placeholder="PMS-12345" {...register('pmsRoomTypeId')} />
          </Field>
        </Row>

        <Field error={errors.status?.message} required>
          <FieldLabel>Status</FieldLabel>
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                onBlur={field.onBlur}
                options={ROOM_TYPE_STATUS_OPTIONS}
                placeholder="Select a status"
                searchable
              />
            )}
          />
        </Field>
      </Section>
    </div>
  );
}
